// v2 fern — a true rosette in two tiers: an outer ring of long fronds that arch
// up and bow low toward the ground, an inner ring of shorter, steeper fronds
// (every frond a two-segment chained blade in alternating greens), all rising
// from a fibrous brown root crown, with two curled fiddleheads poking from the
// centre. Variants re-deal the ring yaws and the outer frond count. Ported 1:1
// from age-of-mcp's v2/tree-fern.ts, using the library's shared `tropic` (blade,
// h01, lerp, tube) helpers.
import * as THREE from "three";
import { defineModel, tropic, type Part } from "../../lib";

const CROWN = "#4d3c28";
const CROWN_CAP = "#5b4a30";
const FROND = "#3c7a34";
const FROND_2 = "#4b9440";
const FROND_NEW = "#56a647";
const SHOOT = "#6f9a4f";
const CURL = "#7fb069";

function fernParts(v: number): Part[] {
  const seed = v * 11 + 7;
  const yaw = v * 1.7;
  const parts: Part[] = [
    // fibrous root crown the rosette springs from
    { geometry: new THREE.CylinderGeometry(0.085, 0.1, 0.1, 6),
      color: CROWN, position: [0, 0.05, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.06, 0), color: CROWN_CAP,
      position: [0, 0.11, 0] },
  ];

  // Outer tier — long fronds arching up then bowing low.
  const NO = 6 + (v % 2);
  for (let i = 0; i < NO; i++) {
    const a = yaw + (i / NO) * Math.PI * 2 + tropic.h01(seed, 10 + i) * 0.5;
    parts.push(...tropic.blade([Math.cos(a) * 0.06, 0.1, Math.sin(a) * 0.06], a, {
      segs: 2, len: tropic.lerp(0.46, 0.56, tropic.h01(seed, 20 + i)), width: 0.13,
      thick: 0.024, rise: tropic.lerp(0.5, 0.7, tropic.h01(seed, 30 + i)), droop: 1.05,
      taper: 0.35,
      colors: i % 2 ? [FROND, FROND_2] : [FROND_2, FROND],
    }));
  }
  // Inner tier — shorter, steeper, brighter young fronds.
  const NI = 4;
  for (let i = 0; i < NI; i++) {
    const a = yaw + 0.45 + (i / NI) * Math.PI * 2 + tropic.h01(seed, 40 + i) * 0.5;
    parts.push(...tropic.blade([Math.cos(a) * 0.03, 0.11, Math.sin(a) * 0.03], a, {
      segs: 2, len: 0.36, width: 0.1, thick: 0.02,
      rise: tropic.lerp(0.95, 1.15, tropic.h01(seed, 50 + i)), droop: 0.75, taper: 0.4,
      colors: [FROND_NEW, FROND_2],
    }));
  }
  // Two curled fiddleheads unfurling from the heart of the rosette.
  for (let i = 0; i < 2; i++) {
    const a = yaw + 1.2 + i * 2.9;
    const tx = Math.cos(a) * 0.055, tz = Math.sin(a) * 0.055;
    parts.push(tropic.tube([tx * 0.4, 0.1, tz * 0.4], [tx, 0.3, tz],
      0.013, 0.009, SHOOT, 4));
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.032, 0),
      color: CURL, position: [tx * 0.7, 0.31, tz * 0.7] });
  }
  return parts;
}

export const treeFernV2 = defineModel({
  id: "tree-fern-v2",
  batchId: "fable",
  name: "Tree fern (v2)",
  tags: ["scenery","tree","fern","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree fern.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.38,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => fernParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => fernParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => fernParts(2) },
  ],
  animations: [],
});
